import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { Loader2, FileText, ExternalLink, Sparkles } from "lucide-react";

/**
 * V232 — Admin tab for the Jendrál-Style Story Generator.
 *
 * Lets an admin fill a tiny form (product A, product B, prices, outcomes,
 * timeframe, CTA URL) and generate + publish a first-person comparison
 * blog article in the viral Marek Jendrál template. The backend uses
 * Claude Sonnet 4.5 via the Emergent LLM Key, so no API-key management
 * is needed on this screen.
 *
 * UX intent:
 *   - 2-column grid for product A vs product B (prices + outcomes).
 *   - Dry-run toggle (`publish: false`) returns the draft JSON without
 *     writing to Mongo — useful for sanity-checking copy before shipping.
 *   - A quick "preset: SPACE Solutions €790 vs €7" button fills the form
 *     with the pilot article inputs so admins can ship in one click.
 */
export default function AdminJendralStoryTab() {
  const [form, setForm] = useState({
    product_a_name: "SPACE Solutions AI SEO Audit",
    product_a_price: 790,
    product_a_outcome_pct: 15,
    product_a_outcome_eur: 908,
    product_b_name: "TopRankPulse AI Snapshot",
    product_b_price: 7,
    product_b_outcome_pct: 320,
    product_b_outcome_eur: 29,
    timeframe: "90 dní",
    cta_url: "/snapshot",
    cta_product_name: "TopRankPulse",
    extra_context:
      "Produkt B navyše ukázal 3× viac AI citation príležitostí a dodal plán do 10 minút.",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [list, setList] = useState([]);
  const [adminToken, setAdminToken] = useState(() => {
    try {
      return localStorage.getItem("admin_token") || "";
    } catch {
      return "";
    }
  });

  const refreshList = async () => {
    if (!adminToken) return;
    try {
      const r = await axios.get(`${API}/admin/jendral-story/list?limit=50`, {
        headers: { "X-Admin-Token": adminToken },
      });
      setList(r.data?.items || []);
    } catch (e) {
      console.warn("jendral list failed", e);
    }
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleGenerate = async (publish) => {
    if (!adminToken) {
      setError("Admin token je potrebný — nastav ho v Admin → Settings");
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        product_a: {
          name: form.product_a_name,
          price_eur: parseFloat(form.product_a_price),
          outcome_pct: parseFloat(form.product_a_outcome_pct) || null,
          outcome_eur: parseFloat(form.product_a_outcome_eur) || null,
        },
        product_b: {
          name: form.product_b_name,
          price_eur: parseFloat(form.product_b_price),
          outcome_pct: parseFloat(form.product_b_outcome_pct) || null,
          outcome_eur: parseFloat(form.product_b_outcome_eur) || null,
        },
        timeframe: form.timeframe,
        cta_url: form.cta_url,
        cta_product_name: form.cta_product_name,
        extra_context: form.extra_context || null,
        publish,
      };
      const r = await axios.post(
        `${API}/admin/jendral-story/generate`,
        payload,
        { headers: { "X-Admin-Token": adminToken } },
      );
      setResult(r.data);
      if (publish) refreshList();
    } catch (e) {
      console.error("generate failed", e);
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
    }
    setSubmitting(false);
  };

  const loadPreset = () => {
    setForm({
      product_a_name: "SPACE Solutions AI SEO Audit",
      product_a_price: 790,
      product_a_outcome_pct: 15,
      product_a_outcome_eur: 908,
      product_b_name: "TopRankPulse AI Snapshot",
      product_b_price: 7,
      product_b_outcome_pct: 320,
      product_b_outcome_eur: 29,
      timeframe: "90 dní",
      cta_url: "/snapshot",
      cta_product_name: "TopRankPulse",
      extra_context:
        "Produkt B navyše ukázal 3× viac AI citation príležitostí a dodal plán do 10 minút.",
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-jendral-story-tab">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Jendrál Story Generator
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Vygeneruje slovenský "ja som zaplatil X vs Y" blog článok cez Claude Sonnet 4.5.
              Publikuje na /blog/ a odošle IndexNow ping pre Bing/Yandex/Seznam.
            </p>
          </div>
          <button
            type="button"
            onClick={loadPreset}
            data-testid="jendral-preset-btn"
            className="shrink-0 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100"
          >
            Nahrať SPACE preset
          </button>
        </div>

        {!adminToken && (
          <div className="mb-3">
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
              Admin Token
            </label>
            <input
              type="password"
              value={adminToken}
              onChange={(e) => {
                setAdminToken(e.target.value);
                try { localStorage.setItem("admin_token", e.target.value); } catch { /* ignore */ }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400"
              placeholder="X-Admin-Token"
              data-testid="jendral-admin-token"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <ProductBox
            side="A"
            name={form.product_a_name}
            price={form.product_a_price}
            outcomePct={form.product_a_outcome_pct}
            outcomeEur={form.product_a_outcome_eur}
            onChange={(k, v) => setField(`product_a_${k}`, v)}
          />
          <ProductBox
            side="B"
            name={form.product_b_name}
            price={form.product_b_price}
            outcomePct={form.product_b_outcome_pct}
            outcomeEur={form.product_b_outcome_eur}
            onChange={(k, v) => setField(`product_b_${k}`, v)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <Field label="Časový rámec" value={form.timeframe} onChange={(v) => setField("timeframe", v)} testid="jendral-timeframe" />
          <Field label="CTA URL" value={form.cta_url} onChange={(v) => setField("cta_url", v)} testid="jendral-cta-url" />
          <Field label="CTA Produkt" value={form.cta_product_name} onChange={(v) => setField("cta_product_name", v)} testid="jendral-cta-product" />
        </div>

        <div className="mt-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
            Extra kontext (voliteľné)
          </label>
          <textarea
            value={form.extra_context}
            onChange={(e) => setField("extra_context", e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400"
            data-testid="jendral-extra-context"
          />
        </div>

        <div className="flex items-center gap-3 mt-5">
          <button
            type="button"
            disabled={submitting}
            onClick={() => handleGenerate(false)}
            data-testid="jendral-dryrun-btn"
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-800 font-bold text-sm hover:bg-gray-200 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Dry-run (nepublikovať)"}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => handleGenerate(true)}
            data-testid="jendral-publish-btn"
            className="px-4 py-2 rounded-lg bg-purple-600 text-white font-bold text-sm hover:bg-purple-700 disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Generujem…</>
            ) : (
              "Generuj + publikuj"
            )}
          </button>
          {error && <span className="text-sm text-red-600" data-testid="jendral-error">{error}</span>}
        </div>
      </div>

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="jendral-result">
          <h3 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {result.published ? "Publikované" : "Dry-run (nepublikované)"}
          </h3>
          <div className="text-sm text-emerald-900 space-y-1">
            <p><strong>Nadpis:</strong> {result.story?.title || result.title}</p>
            {result.story?.excerpt && <p className="text-xs text-emerald-800">{result.story.excerpt}</p>}
            {result.url && (
              <p>
                <a href={result.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-purple-700 underline">
                  Otvoriť článok <ExternalLink className="w-3 h-3" />
                </a>
                {" · "}Word count: <strong>{result.word_count}</strong>
              </p>
            )}
          </div>
          {result.story?.tldr && (
            <ul className="mt-3 list-disc pl-5 text-xs text-emerald-800 space-y-1">
              {result.story.tldr.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="jendral-list-wrap">
        <h3 className="font-bold text-gray-900 mb-3">Vygenerované články ({list.length})</h3>
        {list.length === 0 ? (
          <p className="text-sm text-gray-500">Zatiaľ žiadne. Vygeneruj prvý článok vyššie.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {list.map((p) => (
              <li key={p.post_id} className="py-3 flex items-start justify-between gap-3" data-testid={`jendral-item-${p.slug}`}>
                <div className="min-w-0 flex-1">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-purple-700 hover:underline break-words">
                    {p.title}
                  </a>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {p.word_count} slov · {new Date(p.published_at).toLocaleString("sk-SK")} · {p.views || 0} views
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ProductBox({ side, name, price, outcomePct, outcomeEur, onChange }) {
  const sideColor = side === "A" ? "text-rose-600" : "text-emerald-600";
  const sideBg = side === "A" ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200";
  return (
    <div className={`rounded-xl border ${sideBg} p-4`} data-testid={`jendral-product-${side.toLowerCase()}`}>
      <div className={`text-xs font-black uppercase tracking-widest ${sideColor} mb-3`}>Produkt {side}</div>
      <Field label="Názov" value={name} onChange={(v) => onChange("name", v)} testid={`jendral-${side.toLowerCase()}-name`} />
      <div className="grid grid-cols-3 gap-2 mt-2">
        <Field label="Cena (€)" type="number" value={price} onChange={(v) => onChange("price", v)} testid={`jendral-${side.toLowerCase()}-price`} />
        <Field label="Výsledok %" type="number" value={outcomePct} onChange={(v) => onChange("outcome_pct", v)} testid={`jendral-${side.toLowerCase()}-pct`} />
        <Field label="Výsledok €" type="number" value={outcomeEur} onChange={(v) => onChange("outcome_eur", v)} testid={`jendral-${side.toLowerCase()}-eur`} />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white text-gray-900 placeholder-gray-400"
        data-testid={testid}
      />
    </div>
  );
}
